import { FieldErrors, UseFormRegister } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { AccountFormData } from "@/components/account-form/AccountForm";

interface BankAccountFormSectionProps {
  register: UseFormRegister<AccountFormData>;
  errors: FieldErrors<AccountFormData>;
}

const fields: {
  id: keyof AccountFormData;
  label: string;
  placeholder: string;
  tooltip: string;
}[] = [
  {
    id: "sort_code",
    label: "Sort Code",
    placeholder: "e.g. 12-34-56",
    tooltip: "A 6-digit code used in the UK and Ireland to identify a bank branch.",
  },
  {
    id: "iban",
    label: "IBAN",
    placeholder: "e.g. GB29NWBK60161331926819",
    tooltip: "International Bank Account Number — used to identify a bank account internationally.",
  },
  {
    id: "swift_code",
    label: "Swift/BIC",
    placeholder: "e.g. NWBKGB2L",
    tooltip: "An 8–11 character code that identifies a specific bank internationally.",
  },
  {
    id: "routing_number",
    label: "Routing Number",
    placeholder: "e.g. 021000021",
    tooltip: "A 9-digit number used in the US to identify a financial institution for wire and ACH transfers.",
  },
  {
    id: "transit_number",
    label: "Transit Number",
    placeholder: "e.g. 12345",
    tooltip: "A 5-digit number used in Canada to identify a bank branch.",
  },
  {
    id: "institution_number",
    label: "Institution Number",
    placeholder: "e.g. 001",
    tooltip: "A 3-digit number used in Canada to identify a financial institution.",
  },
];

export function BankAccountFormSection({ register, errors }: BankAccountFormSectionProps) {
  return (
    <>
      {fields.map(({ id, label, placeholder, tooltip }) => (
        <div key={id} className="space-y-2">
          <div className="flex items-center gap-1">
            <Label htmlFor={id}>{label}</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Input
            id={id}
            {...register(id)}
            placeholder={placeholder}
          />
          {errors[id] && (
            <p className="text-sm text-destructive">{errors[id]?.message}</p>
          )}
        </div>
      ))}
    </>
  );
}
